import React, { useEffect, useState, createRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import "./eventupdate.scss";
import Sidebar from "../../components/sidebar/Sidebar";
import Navbar from "../../components/navbar/Navbar";
import Dropzone from 'react-dropzone';
import axios from 'axios';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import TwitterIcon from '@mui/icons-material/Twitter';
import PublicIcon from '@mui/icons-material/Public';
import { eventInputs } from "../../formSource";

const EventUpdate = () => {
  const location = useLocation();
  const id = location.pathname.split("/")[2];

  const [info, setInfo] = useState({});
  const [logoUrl, setLogoUrl] = useState('');
  const [photosUrls, setPhotosUrls] = useState([]);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [collapsed, setCollapsed] = useState(false);  
  const [mapEmbedUrl, setMapEmbedUrl] = useState('');
  const [socials, setSocials] = useState({});

  const navigate = useNavigate();
  const dropzoneRef = createRef();

  const citiesData = [
  'Casablanca', 'Rabat', 'Meknès', 'Marrakech', 'Essaouira', 'Fès', 
  'Tanger', 'Agadir', 'Al Hoceïma', 'Chefchaouen', 'Nador', 'Kénitra', 
  'Asilah', 'Tétouan', 'Ifrane', 'Dakhla'
];

const normalizeCity = (city) => {
  return city
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
};

// Handlers
const handleSocialsChange = (e) => {
    const { id, value } = e.target;
    setSocials((prev) => ({ ...prev, [id]: value }));
  };

  const handleChange = (e) => {
    const { id, value } = e.target;
    
    if (id === 'city') {
      setInfo((prev) => ({ ...prev, [id]: normalizeCity(value) }));
    } else {
      setInfo((prev) => ({ ...prev, [id]: value }));
    }
  };

  useEffect(() => {
  const fetchEvent = async () => {
    try {
      const res = await axios.get(`https://api.moroccool.com/api/events/find/${id}`);
      setInfo(res.data);
      setLogoUrl(res.data.logo || '');
      setPhotosUrls(res.data.photos || []);
      setMapEmbedUrl(res.data.maps || '');
      
      // Fix: Consistent mapping and fallback to individual properties
      setSocials({
        facebook: res.data.socials?.facebook || res.data.facebook || "",
        instagram: res.data.socials?.instagram || res.data.instagram || "",
        x: res.data.socials?.x || res.data.socials?.twitter || res.data.twitter || "",
        tiktok: res.data.socials?.tiktok || res.data.tiktok || "",
        email: res.data.socials?.email || res.data.email || "",
        website: res.data.socials?.website || res.data.website || "",
      });
    } catch (err) {
      console.log("Fetch failed", err);
    }
  };

  fetchEvent();
}, [id]);

  const handlePhotosUpload = async (files) => {
    if (!files.length) return;

    setUploadingPhotos(true);
    const formData = new FormData();

    for (let i = 0; i < files.length; i++) {
      formData.append('images', files[i]);
    }

    try {
      const res = await axios.post("https://api.moroccool.com/api/upload/images", formData);
      setPhotosUrls(prev => [...prev, ...res.data.imageUrls]);
    } catch (err) {
      console.error("Photo upload failed:", err);
    } finally {
      setUploadingPhotos(false);
    }
  };

  const handleSubmit = async (e) => {
  e.preventDefault();

  const updatedEvent = {
    ...info,
    photos: photosUrls,
    maps: mapEmbedUrl,
    // Store in socials object for consistency
    socials: {
      facebook: socials.facebook || '',
      instagram: socials.instagram || '',
      x: socials.x || '',
      tiktok: socials.tiktok || '',
      email: socials.email || '',
      website: socials.website || '',
    },
    // Also store as individual properties for backward compatibility
    facebook: socials.facebook || '',
    instagram: socials.instagram || '',
    twitter: socials.x || '',  // Map x back to twitter for backend
    tiktok: socials.tiktok || '',
    email: socials.email || '',
    website: socials.website || '',
  };

  try {
    await axios.put(`https://api.moroccool.com/api/events/${id}`, updatedEvent);
    navigate("/events");
  } catch (err) {
    console.error("Event update failed:", err);
  }
};

  const openDialog = () => {
    if (dropzoneRef.current) {
      dropzoneRef.current.open();
    }
  };

  return (
    <div className="event-update">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
      <div className="event-update__container">
        <Navbar />
        
        <div className="event-update__content">
          <div className="event-update__header">
            <h1 className="event-update__title">Update Event</h1>
            <p className="event-update__subtitle">Edit your event details and settings</p>
          </div>

          <div className="event-update__form-wrapper">
            <form onSubmit={handleSubmit} className="event-update__form">
              
              {/* Event Media Section */}
              <div className="form-section">
                <div className="form-section__header">
                  <h2 className="form-section__title">Event Media</h2>
                  <p className="form-section__description">Upload photos to showcase your event</p>
                </div>

                <div className="form-field">
                  <label className="form-field__label">Event Gallery</label>
                  <Dropzone
                    onDrop={(acceptedFiles) => handlePhotosUpload(acceptedFiles)}
                    multiple
                    accept="image/*"
                    ref={dropzoneRef}
                    noClick
                    noKeyboard
                  >
                    {({ getRootProps, getInputProps }) => (
                      <div {...getRootProps()} onClick={openDialog} className="upload-zone">
                        <input {...getInputProps()} />
                        <div className="upload-zone__content">
                          <CloudUploadOutlinedIcon className="upload-zone__icon" />
                          <p className="upload-zone__text">Click to upload or drag and drop</p>
                          <p className="upload-zone__subtext">PNG, JPG, GIF up to 10MB</p>
                        </div>
                      </div>
                    )}
                  </Dropzone>

                  {uploadingPhotos && (
                    <div className="upload-status">
                      <div className="upload-status__loader"></div>
                      <span>Uploading photos...</span>
                    </div>
                  )}

                  {photosUrls.length > 0 && (
                    <div className="media-gallery">
                      {photosUrls.map((url, index) => (
                        <div key={index} className="media-gallery__item">
                          <img
                            src={url}
                            alt={`Photo ${index + 1}`}
                            className="media-gallery__image"
                          />
                          <button
                            type="button"
                            onClick={() =>
                              setPhotosUrls((prev) => prev.filter((_, i) => i !== index))
                            }
                            className="media-gallery__remove"
                          >
                            <CloseIcon />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Event Information Section */}
              <div className="form-section">
                <div className="form-section__header">
                  <h2 className="form-section__title">Event Information</h2>
                  <p className="form-section__description">Basic details about your event</p>
                </div>

                <div className="form-grid">
                  {eventInputs.map((input) => {
                    if (input.id === 'city') {
                      return (
                        <div className="form-field" key={input.id}>
                          <label className="form-field__label">{input.label}</label>
                          <select
                            onChange={handleChange}
                            id={input.id}
                            value={info[input.id] || ""}
                            className="form-field__select"
                          >
                            <option value="">Select a city</option>
                            {citiesData.map((city) => (
                              <option key={city} value={normalizeCity(city)}>
                                {city}
                              </option>
                            ))}
                          </select>
                        </div>
                      );
                    }
                    
                    return (
                      <div className="form-field" key={input.id}>
                        <label className="form-field__label">{input.label}</label>
                        <input
                          onChange={handleChange}
                          type={input.type}
                          placeholder={input.placeholder}
                          id={input.id}
                          value={info[input.id] || ""}
                          className="form-field__input"
                        />
                      </div>
                    );
                  })}
                </div>

                <div className="form-field form-field--full">
                  <label className="form-field__label">Description</label>
                  <textarea
                    id='description'
                    rows="4"
                    onChange={handleChange}
                    value={info.description || ''}
                    className="form-field__textarea"
                    placeholder="Describe your event..."
                  />
                </div>
              </div>

              {/* Map Location Section */}
              <div className="form-section">
                <div className="form-section__header">
                  <h2 className="form-section__title">Location</h2>
                  <p className="form-section__description">Add a map to help attendees find your event</p>
                </div>

                <div className="form-field">
                  <label className="form-field__label">Google Maps Embed URL</label>
                  <input
                    type='text'
                    placeholder='https://www.google.com/maps/embed?pb=...'
                    value={mapEmbedUrl}
                    onChange={(e) => setMapEmbedUrl(e.target.value)}
                    className="form-field__input"
                  />
                  <small className="form-field__hint">
                    Get this URL from Google Maps → Share → Embed a map
                  </small>
                </div>

                {mapEmbedUrl && (
                  <div className="map-preview">
                    <iframe
                      src={mapEmbedUrl}
                      width="100%"
                      height="300"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Map Preview"
                    />
                  </div>
                )}
              </div>

              {/* Social Media Section */}
              <div className="form-section">
                <div className="form-section__header">
                  <h2 className="form-section__title">Social Media & Contact</h2>
                  <p className="form-section__description">Connect your social accounts and contact information</p>
                </div>

                <div className="social-fields">
                  <div className="form-field">
                    <label className="form-field__label">
                      <FacebookIcon className="social-icon social-icon--facebook" />
                      Facebook
                    </label>
                    <input 
                      id="facebook" 
                      type="text" 
                      placeholder="https://facebook.com/your-page" 
                      value={socials.facebook || ""} 
                      onChange={handleSocialsChange}
                      className="form-field__input"
                    />
                  </div>

                  <div className="form-field">
                    <label className="form-field__label">
                      <InstagramIcon className="social-icon social-icon--instagram" />
                      Instagram
                    </label>
                    <input 
                      id="instagram" 
                      type="text" 
                      placeholder="https://instagram.com/your-account" 
                      value={socials.instagram || ""} 
                      onChange={handleSocialsChange}
                      className="form-field__input"
                    />
                  </div>

                  <div className="form-field">
                    <label className="form-field__label">
                      <TwitterIcon className="social-icon social-icon--twitter" />
                      Twitter (X)
                    </label>
                    <input 
                      id="x" 
                      type="text" 
                      placeholder="https://x.com/your-account" 
                      value={socials.x || ""} 
                      onChange={handleSocialsChange}
                      className="form-field__input"
                    />
                  </div>

                  <div className="form-field">
                    <label className="form-field__label">
                      <PublicIcon className="social-icon social-icon--website" />
                      Website
                    </label>
                    <input 
                      id="website" 
                      type="url" 
                      placeholder="https://your-website.com" 
                      value={socials.website || ""} 
                      onChange={handleSocialsChange}
                      className="form-field__input"
                    />
                  </div>

                  <div className="form-field">
                    <label className="form-field__label">
                      <span className="social-icon social-icon--email">✉️</span>
                      Email
                    </label>
                    <input 
                      id="email" 
                      type="email" 
                      placeholder="contact@example.com" 
                      value={socials.email || ""} 
                      onChange={handleSocialsChange}
                      className="form-field__input"
                    />
                  </div>

                  <div className="form-field">
                    <label className="form-field__label">
                      <span className="social-icon social-icon--tiktok">🎵</span>
                      TikTok
                    </label>
                    <input 
                      id="tiktok" 
                      type="text" 
                      placeholder="https://tiktok.com/@your-account" 
                      value={socials.tiktok || ""} 
                      onChange={handleSocialsChange}
                      className="form-field__input"
                    />
                  </div>
                </div>
              </div>

              {/* Featured Section */}
              <div className="form-section">
                <div className="form-section__header">
                  <h2 className="form-section__title">Event Settings</h2>
                  <p className="form-section__description">Additional options for your event</p>
                </div>

                <div className="form-field">
                  <label className="form-field__label">Featured Status</label>
                  <select 
                    id="featured" 
                    onChange={handleChange} 
                    value={info.featured || false}
                    className="form-field__select"
                  >
                    <option value={false}>Standard Event</option>
                    <option value={true}>Featured Event</option>
                  </select>
                  <small className="form-field__hint">
                    Featured events get more visibility and appear at the top of listings
                  </small>
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn--primary">
                  Update Event
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventUpdate;